/**
 * PhotoCapture - Componente para captura de foto
 *
 * Suporta:
 * - Camera do dispositivo (mobile e desktop)
 * - Selecao de arquivo da galeria
 * - Preview da foto
 * - Multiplas fotos
 * - Compressao automatica
 */

import { useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  PhotoLibrary as GalleryIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  AddAPhoto as AddPhotoIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

// const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB - para uso futuro
const MAX_DIMENSION = 1200; // Max width/height para compressao
const QUALITY = 0.8; // Qualidade JPEG

/**
 * Comprime uma imagem mantendo proporcoes
 */
const compressImage = (file, maxDimension = MAX_DIMENSION, quality = QUALITY) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        // Calcular novas dimensoes mantendo proporcao
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        // Criar canvas para redimensionar
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Converter para base64
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };

      img.onerror = reject;
      img.src = e.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const PhotoCapture = ({
  onPhotosChange,
  maxPhotos = 5,
  disabled = false,
  initialPhotos = [],
  placeholder = 'Adicionar fotos',
  showCamera = true,
  showGallery = true,
}) => {
  const tokens = usePremiumTokens();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [photos, setPhotos] = useState(initialPhotos);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Processar arquivos selecionados
  const processFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    setIsLoading(true);

    try {
      const newPhotos = [];
      const remainingSlots = maxPhotos - photos.length;

      for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
        const file = files[i];

        // Validar tipo
        if (!file.type.startsWith('image/')) {
          console.warn('Arquivo ignorado (nao e imagem):', file.name);
          continue;
        }

        // Comprimir imagem
        const compressed = await compressImage(file);

        newPhotos.push({
          id: `photo-${Date.now()}-${i}`,
          data: compressed,
          name: file.name,
          timestamp: new Date().toISOString(),
        });
      }

      if (newPhotos.length > 0) {
        const updatedPhotos = [...photos, ...newPhotos];
        setPhotos(updatedPhotos);

        if (onPhotosChange) {
          onPhotosChange(updatedPhotos);
        }
      }
    } catch (error) {
      console.error('Erro ao processar fotos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [photos, maxPhotos, onPhotosChange]);

  // Abrir camera
  const openCamera = useCallback(() => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, []);

  // Abrir galeria
  const openGallery = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Handler de arquivo selecionado
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    e.target.value = ''; // Reset para permitir selecionar mesmo arquivo
  }, [processFiles]);

  // Remover foto
  const removePhoto = useCallback((photoId) => {
    const updatedPhotos = photos.filter((p) => p.id !== photoId);
    setPhotos(updatedPhotos);

    if (onPhotosChange) {
      onPhotosChange(updatedPhotos);
    }
  }, [photos, onPhotosChange]);

  // Abrir preview
  const openPreview = useCallback((photo) => {
    setPreviewPhoto(photo);
  }, []);

  // Fechar preview
  const closePreview = useCallback(() => {
    setPreviewPhoto(null);
  }, []);

  const canAddMore = photos.length < maxPhotos;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Inputs ocultos */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Area de fotos */}
      {photos.length === 0 ? (
        /* Estado vazio */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 4,
            borderRadius: tokens?.radius?.md,
            border: `2px dashed ${tokens?.border?.default}`,
            backgroundColor: tokens?.surface?.elevated,
            minHeight: 150,
          }}
        >
          <AddPhotoIcon sx={{ color: tokens?.text?.tertiary, fontSize: 48, opacity: 0.5 }} />
          <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px' }}>
            {placeholder}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {showCamera && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CameraIcon />}
                onClick={openCamera}
                disabled={disabled || isLoading}
                sx={{
                  borderColor: tokens?.border?.default,
                  color: tokens?.text?.secondary,
                }}
              >
                Camera
              </Button>
            )}
            {showGallery && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<GalleryIcon />}
                onClick={openGallery}
                disabled={disabled || isLoading}
                sx={{
                  borderColor: tokens?.border?.default,
                  color: tokens?.text?.secondary,
                }}
              >
                Galeria
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        /* Grid de fotos */
        <>
          <ImageList
            sx={{
              width: '100%',
              maxHeight: 300,
              overflow: 'auto',
            }}
            cols={3}
            rowHeight={100}
            gap={8}
          >
            {photos.map((photo) => (
              <ImageListItem
                key={photo.id}
                sx={{
                  borderRadius: tokens?.radius?.sm,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
                onClick={() => openPreview(photo)}
              >
                <img
                  src={photo.data}
                  alt={photo.name || 'Foto'}
                  loading="lazy"
                  style={{
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                  }}
                />
                <ImageListItemBar
                  sx={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                  }}
                  position="bottom"
                  actionIcon={
                    <IconButton
                      sx={{ color: 'white' }}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removePhoto(photo.id);
                      }}
                      disabled={disabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                  actionPosition="right"
                />
              </ImageListItem>
            ))}
          </ImageList>

          {/* Botoes para adicionar mais */}
          {canAddMore && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {showCamera && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CameraIcon />}
                  onClick={openCamera}
                  disabled={disabled || isLoading}
                  sx={{
                    borderColor: tokens?.border?.default,
                    color: tokens?.text?.secondary,
                    flex: 1,
                  }}
                >
                  Camera
                </Button>
              )}
              {showGallery && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GalleryIcon />}
                  onClick={openGallery}
                  disabled={disabled || isLoading}
                  sx={{
                    borderColor: tokens?.border?.default,
                    color: tokens?.text?.secondary,
                    flex: 1,
                  }}
                >
                  Galeria
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      {/* Contador */}
      <Typography
        sx={{
          color: tokens?.text?.tertiary,
          fontSize: '12px',
          textAlign: 'center',
        }}
      >
        {photos.length} de {maxPhotos} fotos
      </Typography>

      {/* Dialog de preview */}
      <Dialog
        open={Boolean(previewPhoto)}
        onClose={closePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={closePreview}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewPhoto && (
            <img
              src={previewPhoto.data}
              alt={previewPhoto.name || 'Preview'}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PhotoCapture;
